import { useEffect } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { socketOff, socketOn } from 'lib/clients/socket';
import { receiptDeliveryKey, recordReceiptDelivery } from 'lib/helpers/receiptDelivery';

// socketOn keeps a single handler per event, so every mounted screen shares this listener and
// it is removed only when the last one unmounts.
let listenerCount = 0;
let activeHandler = null;

const startListening = (queryClient) => {
  listenerCount += 1;
  if (listenerCount > 1) return;

  activeHandler = (payload) => recordReceiptDelivery(queryClient, payload);
  socketOn('receiptDelivered', activeHandler);
};

const stopListening = () => {
  listenerCount -= 1;
  if (listenerCount > 0 || !activeHandler) return;

  socketOff('receiptDelivered', activeHandler);
  activeHandler = null;
};

/**
 * Delivery state of a transaction's receipt per channel: `{ email, sms }`, each
 * `{ status, stage, origin }` or undefined when nothing was sent from this session.
 *
 * The practice portal fills it from the `receipt-delivered` data-update. Kiosk and patient
 * screens pass `listenToKiosk` to also take the kiosk room's `receiptDelivered` event, which
 * carries sends made by staff or by the automatic kiosk receipt.
 *
 * @param {number|string} transactionId
 * @param {{ listenToKiosk?: boolean }} [options]
 */
export const useReceiptDelivery = (transactionId, { listenToKiosk = false } = {}) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!listenToKiosk) return undefined;
    startListening(queryClient);
    return stopListening;
  }, [listenToKiosk, queryClient]);

  const { data } = useQuery({
    queryKey: receiptDeliveryKey(transactionId),
    queryFn: () => ({}),
    enabled: false,
    initialData: {},
    staleTime: Infinity
  });

  return data ?? {};
};
